import React from "react";
import {
  formatDateRange,
  isMissingSeatValue,
  seatClass,
  streamsFromSeatMatrix,
} from "./spotRoundData";
import { seatHighlightKey, type SeatCellHighlightMeta } from "./seatChangeHighlight";
import type { SpotAdmissionRound, SpotSeatMatrix, SpotSeatValue } from "./spotRoundTypes";

const RECENT_UPDATE_CLASS = "sr2-cell-recent-update";

type SpotRoundSeatMatrixProps = {
  round: SpotAdmissionRound | undefined;
  highlightedCellKeys?: ReadonlySet<string>;
  highlightMetaByKey?: ReadonlyMap<string, SeatCellHighlightMeta>;
};

function categoriesFromSection(matrix: SpotSeatMatrix, section: "jee" | "mhtcet"): string[] {
  return Object.keys(matrix[section] ?? {}).filter((category) => {
    const row = matrix[section]?.[category] ?? {};
    return Object.values(row).some((value) => seatClass(value) === "avail");
  });
}

function logRenderedDomElement(
  element: HTMLElement | null,
  cellKey: string,
  recentlyUpdated: boolean,
) {
  if (!element || !recentlyUpdated) {
    return;
  }

  console.log("[SpotRoundHighlight] rendered DOM element", {
    cellKey,
    tagName: element.tagName,
    className: element.className,
    inlineStyle: element.getAttribute("style"),
    computedBackground: window.getComputedStyle(element).backgroundColor,
    computedColor: window.getComputedStyle(element).color,
  });
}

type SeatValueDisplayProps = {
  cellKey: string;
  seatValue: SpotSeatValue | string;
  recentlyUpdated: boolean;
  isAvail: boolean;
};

function SeatValueDisplay({
  cellKey,
  seatValue,
  recentlyUpdated,
  isAvail,
}: SeatValueDisplayProps) {
  const useSeatBadge = isAvail || recentlyUpdated;

  if (!useSeatBadge) {
    return <span className="sr2-seat-value">{seatValue}</span>;
  }

  const className = recentlyUpdated && isAvail
    ? `sr2-seat avail ${RECENT_UPDATE_CLASS}`
    : recentlyUpdated
      ? `sr2-seat ${RECENT_UPDATE_CLASS}`
      : "sr2-seat avail";

  if (recentlyUpdated) {
    console.log("[SpotRoundHighlight] render cell", {
      cellKey,
      seatValue,
      recentlyUpdated,
      appliedClassName: className,
    });
    console.log("[SpotRoundHighlight] recentlyUpdated", {
      cellKey,
      recentlyUpdated: true,
    });
  }

  return (
    <span
      ref={(element) => logRenderedDomElement(element, cellKey, recentlyUpdated)}
      className={className}
      data-cell-key={cellKey}
      data-recent-update={recentlyUpdated ? "true" : undefined}
    >
      {seatValue}
    </span>
  );
}

export function SpotRoundSeatMatrix({
  round,
  highlightedCellKeys,
  highlightMetaByKey,
}: SpotRoundSeatMatrixProps) {
  if (!round) {
    return <div className="sr2-empty">No rounds published yet.</div>;
  }

  const range = formatDateRange(round.dayStart, round.dayEnd);
  const columns = streamsFromSeatMatrix(round.matrix);
  const columnLabels = columns.map(
    (courseName) => round.courseShortForms?.[courseName]?.trim() || courseName,
  );

  if (round.status === "Not Started") {
    return (
      <>
        {range ? (
          <div className="sr2-range-line">
            <strong>{range}</strong>
          </div>
        ) : null}
        <div className="sr2-not-started">
          <div className="sr2-not-started__icon" aria-hidden="true">
            ⏳
          </div>
          <div className="sr2-not-started__title">This round hasn&apos;t started yet</div>
          <div className="sr2-not-started__sub">
            {range ? `Seats open ${range}. ` : ""}
            Check back soon.
          </div>
        </div>
      </>
    );
  }

  if (columns.length === 0) {
    const emptyMessage = round.seatsPublished
      ? "No seats are available for your category in this round."
      : "No seat information published for this round yet.";
    return <div className="sr2-empty">{emptyMessage}</div>;
  }

  const renderSeatCell = (
    section: "jee" | "mhtcet",
    category: string,
    stream: string,
    value: SpotSeatValue | undefined,
  ) => {
    const cellKey = seatHighlightKey(section, category, stream);
    const recentlyUpdated = highlightedCellKeys?.has(cellKey) ?? false;
    const highlightMeta = highlightMetaByKey?.get(cellKey);

    if (recentlyUpdated) {
      console.log("[SpotRoundHighlight] recentlyUpdated key match", {
        cellKey,
        courseName: stream,
        exam: section === "jee" ? "JEE" : "MHT-CET",
        category,
        highlightExpiresAt: highlightMeta?.highlightExpiresAt ?? null,
        activeHighlightKeys: highlightedCellKeys ? Array.from(highlightedCellKeys) : [],
      });
    }

    const isZeroOrFull =
      (typeof value === "number" && value === 0)
      || (typeof value === "string" && value.trim().toLowerCase() === "full")
      || Number(value) === 0;

    const displayValue: SpotSeatValue | string =
      isMissingSeatValue(value) || isZeroOrFull ? "—" : (value ?? "—");
    const cls = seatClass(value);
    const isAvail = cls === "avail";

    return (
      <td key={`${section}-${category}-${stream}`}>
        <SeatValueDisplay
          cellKey={cellKey}
          seatValue={displayValue}
          recentlyUpdated={recentlyUpdated}
          isAvail={isAvail}
        />
      </td>
    );
  };

  const renderRows = (section: "jee" | "mhtcet", categories: string[]) =>
    categories.map((category) => (
      <tr key={`${section}-${category}`}>
        <td>{category}</td>
        {columns.map((stream) => {
          const value = round.matrix[section][category]?.[stream];
          return renderSeatCell(section, category, stream, value);
        })}
      </tr>
    ));

  const jeeCategories = categoriesFromSection(round.matrix, "jee");
  const mhtcetCategories = categoriesFromSection(round.matrix, "mhtcet");

  return (
    <>
      {range ? (
        <div className="sr2-range-line">
          <strong>{range}</strong>
        </div>
      ) : null}
      {round.notes ? <div className="sr2-round-notes">{round.notes}</div> : null}
      <div className="sr2-table-scroll">
        <table className="sr2-table">
          <thead>
            <tr>
              <th>Category</th>
              {columns.map((stream, index) => (
                <th key={stream} title={stream}>
                  {columnLabels[index] ?? stream}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jeeCategories.length > 0 ? (
              <>
                <tr className="sr2-group-row">
                  <td>JEE (All India)</td>
                  <td colSpan={columns.length} />
                </tr>
                {renderRows("jee", jeeCategories)}
              </>
            ) : null}
            {mhtcetCategories.length > 0 ? (
              <>
                <tr className="sr2-group-row">
                  <td>MHT-CET</td>
                  <td colSpan={columns.length} />
                </tr>
                {renderRows("mhtcet", mhtcetCategories)}
              </>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className="sr2-update-legend">
        <span className="sr2-update-legend__swatch" aria-hidden="true" />
        <span>Light red highlight = updated in the last 5 minutes</span>
      </div>
    </>
  );
}
